import React from 'react';
import Table, { ColumnConfig } from 'components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import * as util from 'utils/dashboard'; // skipcq: JS-C1003
import CURRENCY from 'utils/currency';
import { formatDate } from 'utils/date-format';
import { AmountChangePercentLabel } from 'utils/status-formatter';
import useStoreName from 'utils/hooks/useStoreName';
import { PAYMENTS } from 'state/payment/type';
import { NA_EMPLOYEE_ID_TOOLTIP } from '../../constants';

interface EmployeeTipsTotalsRow {
  text: string;
  totalTip: number;
}

interface TipSummaryByEmployeeProps {
  summaryData: PAYMENTS.TipByEmployeeAggregate;
  previousStartDate: string;
  previousEndDate: string;
}

const TipSummaryByEmployee = ({
  summaryData,
  previousStartDate,
  previousEndDate,
}: TipSummaryByEmployeeProps): JSX.Element => {
  const tooltipText = `Compared to previous period: ${formatDate(
    previousStartDate,
    'MMM dd yyyy, hh:mm a'
  )} - ${formatDate(previousEndDate, 'MMM dd yyyy, hh:mm a')}`;
  const { getStoreName } = useStoreName();

  const totalTipPercentChange =
    summaryData.previous_total_tip > 0
      ? util.getPercentChange(
          summaryData.total_tip,
          summaryData.previous_total_tip
        )
      : null;

  const tipTotalSummary: EmployeeTipsTotalsRow[] = [
    {
      text: 'TOTAL',
      totalTip: summaryData.total_tip,
    },
  ];

  const totalTipSummaryColumns: ColumnConfig<EmployeeTipsTotalsRow>[] = [
    {
      key: 'text',
      label: '',
      renderCell: (row) => (
        <div className="text-sm font-medium text-gray-blue-80">
          {row.text ? row.text.toUpperCase() : ''}
        </div>
      ),
    },
    {
      key: 'text',
      label: 'Employee ID',
      renderCell: () => <></>,
    },
    {
      key: 'text',
      label: 'Store Name',
      renderCell: () => <></>,
    },
    {
      key: 'totalTip',
      label: 'Tips',
      renderCell: (row) => (
        <div className="flex items-center text-left text-sm font-medium text-gray-blue-80">
          {row.totalTip ? CURRENCY.convertToMainUnit(row.totalTip) : '$0.00'}
          {(totalTipPercentChange || totalTipPercentChange === 0) && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-2 inline-flex">
                    <AmountChangePercentLabel value={totalTipPercentChange} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">{tooltipText}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
    },
  ];

  const employeeListColumns: ColumnConfig<PAYMENTS.IEmployeeTip>[] = [
    {
      key: 'treez_employee_name',
      label: 'Employees',
      sortable: true,
      renderCell: (row) => (
        <div className="capitalize">{row.treez_employee_name}</div>
      ),
      renderHeader: () => (
        <div className="text-sm font-medium text-gray-blue-80">Employees</div>
      ),
    },
    {
      key: 'treez_employee_id',
      label: '',
      renderCell: (row) =>
        row.treez_employee_id ? (
          <span>{row.treez_employee_id.toUpperCase()}</span>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-sm text-gray-blue-60 border-b border-dashed border-gray-blue-40 cursor-help">
                  NA
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">
                {NA_EMPLOYEE_ID_TOOLTIP}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
    },
    {
      key: 'entity_id',
      label: '',
      renderCell: (row) => <div>{getStoreName(row)}</div>,
    },
    {
      key: 'total_tip_amount',
      label: '',
      renderCell: (row) => (
        <div className="text-left text-sm font-medium text-gray-blue-80">
          {row.total_tip_amount
            ? CURRENCY.convertToMainUnit(row.total_tip_amount)
            : '$0.00'}
        </div>
      ),
    },
  ];

  const tableThemeConfig = {
    showTableBorder: true,
    tableGridLayout: '1.3fr 1.3fr 1.3fr 2fr',
  };

  return (
    <>
      <Table
        id="totalEmployeeTipSummary"
        data={tipTotalSummary}
        columns={totalTipSummaryColumns}
        theme={tableThemeConfig}
        customTheme={{
          HeaderCell: `height: 48px;`,
          Cell: `background-color: #f7f8f8;`,
          Table: `border-top-right-radius: 4px; border-top-left-radius: 4px; margin-top: 16px;`,
          Row: `
            &:last-child > td {
              border-bottom-width: 0px;
            }
          `,
        }}
        noDataIndication={<></>}
      />
      <Table
        id="employeeTipsSummary"
        data={summaryData.employee_tips?.sort(
          (a, b) => b.total_tip_amount - a.total_tip_amount
        )}
        columns={employeeListColumns}
        theme={tableThemeConfig}
        customTheme={{
          HeaderCell: `height: 48px; background-color: #fff !important; text-transform: none !important;`,
          Table: `border-bottom-right-radius: 4px; border-bottom-left-radius: 4px;`,
          Row: `
            & > td:first-child {
              border-bottom-left-radius: 4px;
            }
            & > td:last-child {
              border-bottom-right-radius: 4px;
            }
          `,
        }}
      />
    </>
  );
};

export default TipSummaryByEmployee;
